import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export interface LeaderboardEntry {
  driver_id?: string;
  vehicle_id?: string;
  full_name_en?: string;
  driver_code?: string;
  plate_en?: string;
  vehicle_code?: string;
  brand?: string;
  model?: string;
  usage_role?: string;
  fuel_type?: string;
  avg_l_per_100km: number | null;
  total_sar: number;
  total_liters: number;
  fill_count: number;
  distinct_vehicles?: number;
  distinct_drivers?: number;
}

export interface FleetStats {
  fleet_avg_l_per_100km: number | null;
  fleet_total_sar: number | null;
  fleet_total_liters: number | null;
  fleet_fill_count: number;
}

export interface DriverFuelSummary {
  driver: { id: string; code: string; full_name_en: string };
  my_stats: {
    my_avg_l_per_100km: number | null;
    my_total_sar: number;
    my_total_liters: number;
    my_fill_count: number;
  };
  fleet_stats: {
    fleet_avg_l_per_100km: number | null;
    fleet_avg_sar_per_fill: number | null;
  };
  rank: { rank: number; total: number } | null;
  recent_fills: Array<{
    id: string;
    transaction_at: string;
    liters: number;
    total_sar: number;
    liters_per_100km: number | null;
    station_name: string | null;
    vehicle_plate: string | null;
    vehicle_code: string | null;
  }>;
}

export interface InvestorFuelSummary {
  investor: { id: string; code: string; full_name_en: string };
  totals: {
    total_vehicles: number;
    total_sar_30d: number;
    total_liters_30d: number;
    total_fills_30d: number;
  };
  per_vehicle: Array<{
    vehicle_id: string;
    plate_en: string;
    vehicle_code: string;
    avg_l_per_100km: number | null;
    total_sar: number | null;
    total_liters: number | null;
    fill_count: number;
  }>;
}

export function useDriverLeaderboard(opts: { period_days?: number; metric?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['leaderboard', 'drivers', opts],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: LeaderboardEntry[]; fleet: FleetStats; period_days: number; metric: string }>(
        '/api/v1/leaderboard/fuel-efficiency',
        { params: opts }
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useVehicleLeaderboard(opts: { period_days?: number; metric?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['leaderboard', 'vehicles', opts],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: LeaderboardEntry[]; period_days: number; metric: string }>(
        '/api/v1/leaderboard/fuel-efficiency-vehicles',
        { params: opts }
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useMyDriverFuelSummary(enabled: boolean = true) {
  return useQuery({
    queryKey: ['me', 'driver-fuel-summary'],
    queryFn: async () => {
      const { data } = await apiClient.get<DriverFuelSummary>('/api/v1/me/driver-fuel-summary');
      return data;
    },
    enabled,
    staleTime: 60_000,
    retry: false,    // 404 = no driver record, don't retry
  });
}

export function useMyInvestorFuelSummary(enabled: boolean = true) {
  return useQuery({
    queryKey: ['me', 'investor-fuel-summary'],
    queryFn: async () => {
      const { data } = await apiClient.get<InvestorFuelSummary>('/api/v1/me/investor-fuel-summary');
      return data;
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}
